import { useMemo } from "react";

import { type ChartDataset } from "chart.js";
import clsx from "clsx";
import copy from "copy-to-clipboard";
import { cloneDeep, omit } from "lodash";
import { Line } from "react-chartjs-2";
import LinesEllipsis from "react-lines-ellipsis";
import { useToasts } from "react-toast-notifications";
import TinyColor from "tinycolor2";

import northbeamLogo from "@assets/northbeam-logo.svg";
import { Loading } from "@components/title-slide-view";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  type BenchmarkIndicatorRow,
  UniversalBenchmarksIndicator,
  useUniversalBenchmarks,
} from "@features/universal-benchmarks";
import { useShowV3SalesPage } from "@hooks/http";
import { prettyLogEvent } from "@internal/analytics";
import {
  CreativesStatusEnum,
  type FetchCreatives_me_creatives_rows,
  type FetchMetricPercentiles_me_metricPercentiles_metrics,
  MAX_NUMBER,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import {
  CustomJsonCompressedParam,
  emptyText,
  jsonHash,
  metrics,
  slugify,
} from "@north-beam/nb-common";
import { overviewTransactionsAndCac } from "@pages/sales/saved-views/sales-views-templates/overview-transactions-and-cac";
import { Badge } from "@shared/badge";
import { MetricValue } from "@shared/metric-value";
import Spinner from "@shared/spinner";

import {
  type AdColouring,
  useCurrentNumberOfAdsAtom,
  usePreviewAdAtom,
} from "../atoms";
import {
  type ReportBodyState,
  useReportBodyState,
} from "../hooks/ca-control-state";
import {
  getCreativeType,
  getPlatformMetricFromAd,
  getPlatformName,
} from "../utils/platform-metrics";

import { type MetricPercentileByMetricId } from "./ca-card-sleeve";
import {
  AppLovinIcon,
  FBIcon,
  GoogleIcon,
  PinterestIcon,
  SnapchatIcon,
  TikTokIcon,
  YoutubeIcon,
} from "./icons";

export const statusColourMapping = (status: CreativesStatusEnum | null) => {
  if (status === CreativesStatusEnum.inactive) {
    return "#E9411F";
  }
  if (status === CreativesStatusEnum.active) {
    return "#68BE56";
  }
  return "#808997";
};

export function getIconForPlatform(ad: FetchCreatives_me_creatives_rows) {
  const platform = JSON.parse(ad.adKey).platform;
  if (platform === "pinterest") {
    return PinterestIcon;
  }
  if (platform === "fb") {
    return FBIcon;
  }
  if (platform === "applovin") {
    return AppLovinIcon;
  }
  if (platform === "snapchat") {
    return SnapchatIcon;
  }

  const platformName = getPlatformName(ad);
  if (platformName === "YouTube Ads") {
    return YoutubeIcon;
  }
  if (platformName === "Google Ads") {
    return GoogleIcon;
  }

  return TikTokIcon;
}

export function CreativeAnalyticsCard({
  ad,
  availableMetricsById,
  availableAdColours,
  heroMetricId,
  attributionModel,
  attributionWindow,
  selectedMetrics,
  metricPercentileByMetricIdAndPlatform,
  metricPercentilesLoading,
  metricsBySalesTableMetridId,
}: {
  ad: FetchCreatives_me_creatives_rows;
  availableMetricsById: Record<string, SalesPageMetric>;
  availableAdColours: AdColouring[];
  heroMetricId: string;
  attributionModel: ReportBodyState["attributionModel"];
  attributionWindow: ReportBodyState["attributionWindow"];
  selectedMetrics: ReportBodyState["metrics"];
  metricPercentileByMetricIdAndPlatform: MetricPercentileByMetricId;
  metricPercentilesLoading: boolean;
  metricsBySalesTableMetridId: Record<string, SalesPageMetric>;
}) {
  const showV3SalesPage = useShowV3SalesPage();
  const { unparsedDateRange } = useReportBodyState();
  const metricsArray = Object.values(availableMetricsById);

  const aggregratedMetricsById = useMemo(() => {
    const agg = {} as Record<string, number>;

    if (!ad.dailyMetrics?.length) {
      return agg;
    }

    const aggregatedMetrics = ad.dailyMetrics
      .slice(1, ad.dailyMetrics.length)
      ?.reduce((acc, dailyRow) => {
        const metricPoint = metrics.makeMetricPoint(dailyRow, null);

        metrics.addToMetricPoint(acc, metricPoint);
        return acc;
      }, metrics.makeMetricPoint(cloneDeep(ad.dailyMetrics[0]), null));

    metricsArray.forEach((metric) => {
      if (!agg[metric.id]) {
        agg[metric.id] = 0;
      }
      agg[metric.id] += metric.calculate(aggregatedMetrics);
    });
    Object.keys(agg).forEach((metricId) => {
      const metricSpec = availableMetricsById[metricId];
      if (!metricSpec.requiredNBCalculatedMetrics?.length) {
        return;
      }

      const modifiedDay = (
        metricSpec?.requiredNBCalculatedMetrics || []
      ).reduce((prev, requiredMetric) => {
        const reqMetricSpec =
          availableMetricsById[requiredMetric] ??
          metricsBySalesTableMetridId[requiredMetric];
        return {
          ...prev,
          [requiredMetric]: reqMetricSpec.calculate(aggregatedMetrics),
        };
      }, aggregatedMetrics);

      agg[metricSpec.id] = metricSpec.calculate(modifiedDay);
    });

    return agg;
  }, [
    ad.dailyMetrics,
    availableMetricsById,
    metricsArray,
    metricsBySalesTableMetridId,
  ]);
  const [, setAd] = usePreviewAdAtom();
  const { user } = useUser();
  const toast = useToasts();

  const [currentNumberOfAds, setCurrentNumberOfAds] =
    useCurrentNumberOfAdsAtom();
  const Icon = getIconForPlatform(ad);
  const { groupAdsByName, selectedAdsByAdKey, setSelectedAdsByAdKey } =
    useReportBodyState();

  const isSelected = Boolean(selectedAdsByAdKey[ad.adKey]);
  const numberOfCurrentlySelectedAds = Object.keys(selectedAdsByAdKey).length;

  const salesPageUrl = useMemo(() => {
    const url = new URL(`${window.location.origin}`);
    const adKey = JSON.parse(ad.adKey);
    if (showV3SalesPage) {
      url.searchParams.set("attributionModel", attributionModel);
      url.searchParams.set("attributionWindow", attributionWindow);
      url.searchParams.set("nbAccountingMode", "accrual");
      url.searchParams.set("granularity", "ad");
      url.searchParams.set("timeGranularity", "daily");
      url.searchParams.set("breakdowns", "[]");
      url.searchParams.set("flyoutId", slugify(adKey));
      const searchQuery = CustomJsonCompressedParam().encode({
        method: "text",
        operator: "is",
        query: ad.adKey,
      }) as string;
      url.searchParams.set("activeTab", "ad");
      url.searchParams.set("searchQuery", searchQuery);
      url.searchParams.set("dateRange", JSON.stringify(unparsedDateRange));

      // use a default saved view
      url.searchParams.set("savedViewId", "default");
    } else {
      url.searchParams.set("attributionModel", attributionModel);
      url.searchParams.set("attributionWindow", attributionWindow);
      url.searchParams.set("nbAccountingMode", "accrual");
      url.searchParams.set("granularity", "ad");
      url.searchParams.set("timeGranularity", "daily");
      url.searchParams.set("breakdowns", "[]");
      url.searchParams.set("flyoutPath", slugify(adKey));
      url.searchParams.set("searchValue", jsonHash({ nbt: adKey }));
      url.searchParams.set("dateRange", JSON.stringify(unparsedDateRange));

      // use a default saved view
      url.searchParams.set("savedView", overviewTransactionsAndCac.id);
    }

    url.pathname = `${user.displayUserId}/sales`;

    return url.toString();
  }, [
    ad.adKey,
    showV3SalesPage,
    user.displayUserId,
    attributionModel,
    attributionWindow,
    unparsedDateRange,
  ]);

  return (
    <div className="relative bg-v2-primary">
      <div
        className={clsx(
          "w-[18rem] rounded-2xl border border-v2-secondary hover:border-v2-primary",
          "hover:border-3 cursor-pointer p-[2.2px] hover:p-0 overflow-hidden relative",
        )}
        onClick={() => {
          setAd(ad);
          prettyLogEvent.navigateTo({
            page: Pages.CREATIVE_ANALYTICS,
            user,
            domain: "Ad Preview Modal",
            data: { ad },
          });
        }}
      >
        <div className=" w-full flex flex-col  rounded-2xl relative ">
          <div className="relative absolute">
            <div
              onClick={(e) => {
                e.stopPropagation();

                if (
                  !selectedAdsByAdKey[ad.adKey] &&
                  numberOfCurrentlySelectedAds >= 6
                ) {
                  return;
                }
                if (isSelected) {
                  setSelectedAdsByAdKey(omit(selectedAdsByAdKey, ad.adKey));
                  prettyLogEvent.onClick({
                    user,
                    page: Pages.CREATIVE_ANALYTICS,
                    additionalActionInfo: "remove-from-charts",
                  });
                } else {
                  const [firstAdColour] = availableAdColours;
                  setSelectedAdsByAdKey({
                    ...selectedAdsByAdKey,
                    [ad.adKey]: {
                      adKey: ad.adKey,
                      adColouring: firstAdColour,
                      adVisualId: `Ad ${currentNumberOfAds}`,
                    },
                  });

                  setCurrentNumberOfAds(currentNumberOfAds + 1);
                  prettyLogEvent.onClick({
                    page: Pages.CREATIVE_ANALYTICS,
                    user,
                    additionalActionInfo: "ad-to-charts",
                  });
                }
              }}
              className={clsx(
                "absolute top-3 left-3 px-3 py-1 rounded-2xl bg-v2-secondary",
                "flex flex-row items-center shadow-[0_0_0.15rem_#808897]",
              )}
            >
              <input
                type="checkbox"
                disabled={
                  !selectedAdsByAdKey[ad.adKey] &&
                  numberOfCurrentlySelectedAds >= 6
                }
                checked={isSelected}
                className="mr-2 accent-cosmic w-3 h-3 "
                onChange={() => {
                  //do nothing
                }}
              />
              <div className="text-xs">Show in charts</div>
            </div>
            {selectedAdsByAdKey[ad.adKey] && (
              <div
                className={clsx(
                  "absolute top-3 left-[8.5rem] rounded-2xl px-3 py-1",
                  "bg-v2-secondary flex flex-row items-center shadow-[0_0_0.15rem_#808897]",
                )}
              >
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{
                    backgroundColor:
                      selectedAdsByAdKey[ad.adKey].adColouring
                        .pointBackgroundColor,
                  }}
                />
                <div className="text-xs ml-1">
                  {selectedAdsByAdKey[ad.adKey].adVisualId}
                </div>
              </div>
            )}
            <a
              className={clsx(
                "absolute top-3 right-10 w-6 h-6 bg-v2-secondary rounded-full text-v2-action",
                "justify-center flex items-center no-underline hover:text-cosmic+1",
                "hover:bg-v2-action-tertiary shadow-[0_0_0.15rem_#808897]",
              )}
              target="_blank"
              href={salesPageUrl}
              onClick={(e) => {
                e.stopPropagation();
                prettyLogEvent.navigateToPage({
                  page: Pages.CREATIVE_ANALYTICS,
                  user,
                  additionalActionInfo: "sales-page",
                  data: { url: salesPageUrl },
                });
              }}
              rel="noreferrer"
            >
              <GenericTooltip
                noInfoCircle
                className=" flex items-center"
                content="View in sales page"
              >
                <i className="fa-light fa-arrow-up-right-from-square text-xs" />
              </GenericTooltip>
            </a>
            <Icon className="absolute top-3 right-3 shadow-[0_0_0.15rem_#808897]" />
          </div>
          {!ad.imageUri && (
            <div
              className={clsx(
                "h-[14rem] rounded-t-2xl flex justify-center",
                "items-center bg-v2-secondary -mx-1 -mt-1",
              )}
            >
              <GenericTooltip
                content={
                  "This is likely because the account responsible for this creative " +
                  "is no longer connected to Northbeam"
                }
                infoCircleClassName="text-v2-primary"
              >
                Not available
              </GenericTooltip>
            </div>
          )}
          {ad.imageUri && (
            <div className="h-[14rem] overflow-hidden flex justify-center bg-v2-gray  -mx-1 -mt-1">
              <img
                className="object-scale-down h-[14rem]"
                src={ad.imageUri}
                alt={`${ad.name} ad`}
              />
            </div>
          )}
          <div className="mx-4  mt-5 text-sm  ">
            <GenericTooltip content={ad.name} noInfoCircle>
              {groupAdsByName ? (
                <div className="truncate font-semibold">{ad.name ?? "N/A"}</div>
              ) : (
                <div className="flex flex-row items-center">
                  <Badge
                    className="w-[14.5rem] mr-1"
                    textClassname="ml-1 "
                    text={ad.name ?? "N/A"}
                    badgeColor={statusColourMapping(ad.status)}
                    size="md"
                  />
                  <i
                    className="fa-light fa-clone hover:text-primary text-v2-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      toast.addToast("COPIED AD NAME TO CLIPBOARD", {
                        appearance: "success",
                      });
                      ad.name && copy(ad.name);
                      prettyLogEvent.onClick({
                        user,
                        page: Pages.CREATIVE_ANALYTICS,
                        additionalActionInfo: "copy-ad-name",
                        data: { name: ad.name },
                      });
                    }}
                  />
                </div>
              )}
            </GenericTooltip>
            <AdNaming ad={ad} />
            <PlatformNBMetricComparator
              heroMetricId={heroMetricId}
              metricsById={availableMetricsById}
              aggregratedMetricsById={aggregratedMetricsById}
              ad={ad}
              attributionModel={attributionModel}
              attributionWindow={attributionWindow}
            />
            <SpendToRevComparator
              metricsById={availableMetricsById}
              aggregratedMetricsById={aggregratedMetricsById}
              attributionModel={attributionModel}
              attributionWindow={attributionWindow}
            />
            <Metrics
              aggregratedMetricsById={aggregratedMetricsById}
              metricsById={availableMetricsById}
              ad={ad}
              selectedMetrics={selectedMetrics}
              metricPercentileByMetricIdAndPlatform={
                metricPercentileByMetricIdAndPlatform
              }
              metricPercentilesLoading={metricPercentilesLoading}
              attributionModel={attributionModel}
              attributionWindow={attributionWindow}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function AdNaming({ ad }: { ad: FetchCreatives_me_creatives_rows }) {
  const adset = ad.adsetName ?? emptyText;
  const campaign = ad.campaignName ?? emptyText;

  return (
    <div className="flex flex-col space-y-1 mt-2">
      <div className="truncate">
        <span className="text-gray mr-2">Adset</span>
        <GenericTooltip noInfoCircle content={adset}>
          {adset}
        </GenericTooltip>
      </div>
      <div className="truncate">
        <span className="text-gray mr-2">Campaign</span>
        <GenericTooltip noInfoCircle content={campaign}>
          {campaign}
        </GenericTooltip>
      </div>
    </div>
  );
}

const colourMapping = {
  good: "bg-v2-highlight-notice",
  medium: "bg-v2-highlight-pending",
  bad: "bg-v2-highlight-warm",
} as const;

function Metrics({
  metricsById,
  aggregratedMetricsById,
  selectedMetrics,
  ad,
  metricPercentileByMetricIdAndPlatform,
  metricPercentilesLoading,
  attributionModel,
  attributionWindow,
}: {
  ad: FetchCreatives_me_creatives_rows;
  metricsById: Record<string, SalesPageMetric>;
  aggregratedMetricsById: Record<string, number>;
  selectedMetrics: Set<string>;
  metricPercentileByMetricIdAndPlatform: MetricPercentileByMetricId;
  metricPercentilesLoading: boolean;
  attributionModel: ReportBodyState["attributionModel"];
  attributionWindow: ReportBodyState["attributionWindow"];
}) {
  const metricsArray = useMemo(() => [...selectedMetrics], [selectedMetrics]);

  return (
    <div className="mt-2 w-full grid grid-cols-2 gap-2 text-xs mb-3 ">
      {metricsArray.map((metric) => (
        <Metric
          key={metric}
          metric={metric}
          metricsById={metricsById}
          aggregratedMetricsById={aggregratedMetricsById}
          ad={ad}
          metricPercentileByMetricIdAndPlatform={
            metricPercentileByMetricIdAndPlatform
          }
          metricPercentilesLoading={metricPercentilesLoading}
          attributionModel={attributionModel}
          attributionWindow={attributionWindow}
        />
      ))}
    </div>
  );
}

const supportedPercentiles = [
  0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95,
  100,
] as const;
type PercentileColorMap = {
  [key in (typeof supportedPercentiles)[number]]: string;
};

function createColorScale(positiveIsGood: boolean): PercentileColorMap {
  const colorScale: PercentileColorMap = {} as PercentileColorMap;

  const startColor = new TinyColor(!positiveIsGood ? "#218F27" : "#E9411F");
  const endColor = new TinyColor(positiveIsGood ? "#218F27" : "#E9411F");
  const middleColor = new TinyColor("#FFD247");

  for (const percentile of supportedPercentiles) {
    let color: TinyColor.Instance;

    if (percentile < 50) {
      const mixAmount = (percentile / 50) * 100;
      color = TinyColor.mix(startColor, middleColor, mixAmount);
    } else {
      const mixAmount = ((percentile - 50) / 50) * 100;
      color = TinyColor.mix(middleColor, endColor, mixAmount);
    }

    colorScale[percentile] = color.toHexString();
  }

  return colorScale;
}

function createDataBasedColorScale(
  colorScale: PercentileColorMap,
  percentiles: FetchMetricPercentiles_me_metricPercentiles_metrics,
) {
  if (!percentiles) {
    return supportedPercentiles.map((percentile) => ({
      color: colorScale[percentile],
      percentile,
    }));
  }

  let previousPercentile: (typeof supportedPercentiles)[number] | null = null;
  return supportedPercentiles.map((percentile) => {
    if (
      previousPercentile &&
      percentiles[`p${percentile}`] <= percentiles[`p${previousPercentile}`]
    ) {
      return { color: colorScale[previousPercentile], percentile };
    }
    previousPercentile = percentile;
    return { color: colorScale[percentile], percentile };
  });
}

function extractPercentile(
  inputValue: number,
  metricPercentiles: FetchMetricPercentiles_me_metricPercentiles_metrics,
) {
  if (!metricPercentiles || isNaN(inputValue)) {
    return null;
  }

  for (const percentile of supportedPercentiles) {
    const percentileKey = `p${percentile}` as const;
    if (inputValue <= metricPercentiles[percentileKey]) {
      return percentile;
    }
  }
  return 100;
}

function Metric({
  aggregratedMetricsById,
  metric,
  metricsById,
  ad,
  metricPercentileByMetricIdAndPlatform,
  metricPercentilesLoading,
  attributionModel,
  attributionWindow,
}: {
  aggregratedMetricsById: Record<string, number>;
  metric: string;
  metricsById: Record<string, SalesPageMetric>;
  ad: FetchCreatives_me_creatives_rows;
  metricPercentileByMetricIdAndPlatform: MetricPercentileByMetricId;
  metricPercentilesLoading: boolean;
  attributionModel: ReportBodyState["attributionModel"];
  attributionWindow: ReportBodyState["attributionWindow"];
}) {
  const value = aggregratedMetricsById[metric];
  const metricSpec = metricsById[metric];
  const platformName = getPlatformName(ad);

  const { heatMap, dataBasedColourScale, percentileToUse } = useMemo(() => {
    const platform = getCreativeType(ad);

    const metricPercentiles =
      metricPercentileByMetricIdAndPlatform[`${platform}-${metric}`];

    const percentileToUse =
      extractPercentile(value, metricPercentiles?.percentiles) ?? 50;

    const heatMap = (
      percentileToUse === 50
        ? "medium"
        : (percentileToUse > 50 && metricSpec.isPositiveChangeGood) ||
          (percentileToUse < 50 && !metricSpec.isPositiveChangeGood)
        ? "good"
        : "bad"
    ) as keyof typeof colourMapping;

    const colourScale = createColorScale(metricSpec.isPositiveChangeGood);

    const dataBasedColourScale = createDataBasedColorScale(
      colourScale,
      metricPercentiles?.percentiles,
    );

    return { heatMap, dataBasedColourScale, percentileToUse };
  }, [
    ad,
    metric,
    metricPercentileByMetricIdAndPlatform,
    metricSpec.isPositiveChangeGood,
    value,
  ]);

  const benchmark = useUniversalBenchmarks({
    level: "campaign",
    attributionModel,
    attributionWindow,
    accountingMode: "accrual",
    breakdowns: [{ "Platform (Northbeam)": platformName }],
    filters: [],
    metric: metricSpec
      ? {
          id: metricSpec.metadata.baseId,
          isLtv: metricSpec.metadata.isLtv,
        }
      : undefined,
  });

  const benchmarkIndicatorRow = useMemo(() => {
    if (!benchmark || benchmark?.status === "loading") {
      return null;
    }

    return {
      name:
        metricSpec.name ??
        metricSpec.metadata.name ??
        metricSpec.spec?.name ??
        metricSpec.metadata.salesTableMetricId,
      current: value,
      target: benchmark.value,
      format: metricSpec.format,
      positiveTrend: metricSpec.isPositiveChangeGood ? "up" : "down",
    } as BenchmarkIndicatorRow;
  }, [benchmark, value, metricSpec]);

  if (metricPercentilesLoading) {
    return (
      <div
        key={metric}
        className={clsx(
          "flex flex-row justify-between w-full h-[2.75rem] border border-v2-secondary rounded-md",
          "items-center px-2 relative",
        )}
      >
        <Loading className="w-9" />
      </div>
    );
  }

  const content = (
    <>
      <LinesEllipsis
        className="font-semibold"
        text={metricSpec.name}
        maxLine="2"
        ellipsis="..."
        trimRight
        basedOn="letters"
      />
      <div className="flex items-center gap-1">
        <MetricValue
          as="div"
          className="whitespace-nowrap"
          value={value}
          format={metricSpec.format}
          precision="exact"
        />
        {benchmark?.status === "loading" && (
          <Spinner colorClassname="w-3 h-3 text-cosmic text-xxs" />
        )}
      </div>
      <ColourScale
        dataBasedColourScale={dataBasedColourScale}
        percentileToUse={percentileToUse}
      />
    </>
  );

  if (benchmarkIndicatorRow) {
    return (
      <UniversalBenchmarksIndicator
        key={metric}
        className={clsx(
          "flex flex-row justify-between w-full h-[2.75rem] border border-v2-secondary rounded-md items-center",
          "px-2 relative overflow-hidden",
          {
            [colourMapping[heatMap]]: true,
          },
        )}
        benchmarkIndicatorRows={[benchmarkIndicatorRow]}
        iconsSize="sm"
      >
        {content}
      </UniversalBenchmarksIndicator>
    );
  } else {
    return (
      <GenericTooltip
        key={metric}
        className={clsx(
          "flex flex-row justify-between w-full h-[2.75rem] border border-v2-secondary rounded-md items-center",
          "px-2 relative overflow-hidden",
          {
            [colourMapping[heatMap]]: true,
          },
        )}
        content={
          <div className="flex items-center gap-2 justify-between">
            <div>{metricSpec.name}</div>
            <MetricValue
              as="div"
              className="whitespace-nowrap font-bold"
              value={value}
              format={metricSpec.format}
              precision="exact"
            />
          </div>
        }
        noInfoCircle
      >
        {content}
      </GenericTooltip>
    );
  }
}

function ColourScale({
  dataBasedColourScale,
  percentileToUse,
}: {
  dataBasedColourScale: {
    color: string;
    percentile: (typeof supportedPercentiles)[number];
  }[];
  percentileToUse: (typeof supportedPercentiles)[number];
}) {
  return (
    <div className="absolute bottom-0 flex flex-row w-full h-1.5 items-stretch left-0">
      {dataBasedColourScale.map(({ percentile, color }) => {
        return (
          <div
            className="w-full relative"
            key={percentile}
            style={{ backgroundColor: color }}
          >
            {percentile === percentileToUse && (
              <div
                className={clsx(
                  "z-1 absolute top-[0.2px] w-[0.4rem] h-[0.4rem] bg-black",
                  "border-white border rounded-full",
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function PlatformNBMetricComparator({
  metricsById,
  aggregratedMetricsById,
  ad,
  heroMetricId,
  attributionModel,
  attributionWindow,
}: {
  metricsById: Record<string, SalesPageMetric>;
  aggregratedMetricsById: Record<string, number>;
  ad: FetchCreatives_me_creatives_rows;
  heroMetricId: string;
  attributionModel: ReportBodyState["attributionModel"];
  attributionWindow: ReportBodyState["attributionWindow"];
}) {
  const metric = metricsById[heroMetricId];
  const { user } = useUser();

  const Icon = getIconForPlatform(ad);
  const platformName = getPlatformName(ad);

  const nbChartData = useMemo(() => {
    const dailyMetrics = ad?.dailyMetrics;
    const metricSpec = metric as SalesPageMetric | undefined;

    if (!dailyMetrics?.length || !metricSpec) {
      return {
        data: new Array<number>(dailyMetrics?.length ?? 10).fill(0),
        average: 0,
      };
    }

    let total = 0;
    let validCounts = 0;
    const adData = dailyMetrics.map((metricDay) => {
      const calculated = metricSpec.calculate(metricDay);
      const value = calculated < MAX_NUMBER ? calculated : MAX_NUMBER;

      if (!(isNaN(value) || !isFinite(value))) {
        total += value;
        validCounts++;
      }
      return value;
    });

    return { data: adData, average: validCounts ? total / validCounts : 0 };
  }, [ad?.dailyMetrics, metric]);

  const platformChartData = useMemo(() => {
    const dailyMetrics = ad?.dailyMetrics;
    const platformMetricSpec = getPlatformMetricFromAd(ad, metric, metricsById);

    if (!dailyMetrics?.length || !platformMetricSpec) {
      return {
        data: new Array<number>(dailyMetrics?.length ?? 10).fill(0),
        average: 0,
      };
    }

    let total = 0;
    let validCounts = 0;
    const adData = dailyMetrics.map((metricDay) => {
      const calculated = platformMetricSpec.calculate(metricDay);
      const value = calculated < MAX_NUMBER ? calculated : MAX_NUMBER;
      if (!(isNaN(value) || !isFinite(value))) {
        total += value;
        validCounts++;
      }
      return value;
    });

    return {
      data: adData,
      average: validCounts ? total / validCounts : 0,
      platformMetric: platformMetricSpec,
    };
  }, [ad, metric, metricsById]);

  const benchmark = useUniversalBenchmarks({
    level: "campaign",
    attributionModel,
    attributionWindow,
    accountingMode: "accrual",
    breakdowns: [{ "Platform (Northbeam)": platformName }],
    filters: [],
    metric: metric
      ? { id: metric.metadata.baseId, isLtv: metric.metadata.isLtv }
      : undefined,
  });

  const benchmarkIndicatorRow = useMemo(() => {
    const value = aggregratedMetricsById[heroMetricId];

    if (!benchmark || benchmark?.status === "loading") {
      return null;
    }

    return {
      name:
        metric.name ??
        metric.metadata.name ??
        metric.spec?.name ??
        metric.metadata.salesTableMetricId,
      current: value,
      target: benchmark.value,
      format: metric.format,
      positiveTrend: metric.isPositiveChangeGood ? "up" : "down",
    } as BenchmarkIndicatorRow;
  }, [benchmark, aggregratedMetricsById, heroMetricId, metric]);

  if (!metric) {
    return null;
  }

  const formattedApproxDisplay = metric.formatter(
    aggregratedMetricsById[heroMetricId],
    user.currencies.currentCurrency,
    { precision: "approximate" },
  );

  return (
    <div className="border border-v2-secondary h-[4.5rem] w-full rounded-md mt-4 p-2 flex flex-row items-center">
      <div className="flex flex-col w-[6.8rem]">
        {benchmarkIndicatorRow ? (
          <UniversalBenchmarksIndicator
            className="flex items-center gap-1"
            iconsSize="sm"
            benchmarkIndicatorRows={[benchmarkIndicatorRow]}
          >
            <div className="flex items-center gap-2">
              <div className="h-6 font-semibold truncate">{metric.name}</div>
            </div>
          </UniversalBenchmarksIndicator>
        ) : (
          <GenericTooltip
            noInfoCircle
            content={
              <div className="flex items-center gap-2 justify-between">
                <div>{metric.name}</div>
                <MetricValue
                  as="div"
                  className="whitespace-nowrap font-bold"
                  value={aggregratedMetricsById[heroMetricId]}
                  format={metric.format}
                  precision="exact"
                />
              </div>
            }
          >
            <div className="flex items-center gap-2">
              <div className="h-6 font-semibold truncate">{metric.name}</div>
              {benchmark?.status === "loading" && (
                <Spinner colorClassname="w-3 h-3 text-cosmic text-xxs" />
              )}
            </div>
          </GenericTooltip>
        )}
        <div className="flex flex-row items-center">
          <div className="w-7 h-7 bg-northbeam-icon rounded-full justify-center flex items-center">
            <img className="w-4" alt="northbeam-logo" src={northbeamLogo} />
          </div>
          <div className={clsx("ml-1", createFontSize(formattedApproxDisplay))}>
            {formattedApproxDisplay}
          </div>
          <div className="w-[2rem] h-[1.5rem] ml-2">
            <MiniGraph
              data={nbChartData.data}
              average={nbChartData.average}
              isPositiveChangeGood={metric.isPositiveChangeGood}
            />
          </div>
        </div>
      </div>
      <div className="h-[1rem] mx-2 border-l-[1.5px] border-#C1C5CD self-end mb-1" />
      <div className="flex flex-col  w-[6.8rem]">
        <GenericTooltip
          noInfoCircle
          content={`${
            platformChartData.platformMetric?.name
          } - ${platformChartData.platformMetric?.formatter(
            aggregratedMetricsById[platformChartData.platformMetric.id],
            user.currencies.currentCurrency,
            { precision: "exact" },
          )}`}
        >
          <div className="h-6 font-semibold truncate">
            {platformChartData.platformMetric?.name}
          </div>
        </GenericTooltip>
        <div className="flex flex-row items-center">
          <Icon />
          {platformChartData.platformMetric ? (
            <>
              <MetricValue
                as="div"
                value={
                  aggregratedMetricsById[platformChartData.platformMetric.id]
                }
                format={platformChartData.platformMetric.format}
                precision="approximate"
                className={clsx(
                  "ml-1",
                  createFontSize(
                    platformChartData.platformMetric.formatter(
                      aggregratedMetricsById[
                        platformChartData.platformMetric.id
                      ],
                      user.currencies.currentCurrency,
                      { precision: "approximate" },
                    ),
                  ),
                )}
              />
              <div className="w-[2rem] h-[1.5rem] ml-2">
                <MiniGraph
                  data={platformChartData.data}
                  average={platformChartData.average}
                  isPositiveChangeGood={
                    platformChartData.platformMetric.isPositiveChangeGood
                  }
                />
              </div>
            </>
          ) : (
            <div className="w-[4.5rem] text-gray text-center">-- --</div>
          )}
        </div>
      </div>
    </div>
  );
}

function createFontSize(text: string) {
  return {
    "text-sm": text.length <= 5,
    "text-xs": text.length > 5 && text.length <= 6,
    "text-xxs": text.length > 6 && text.length <= 7,
    "text-xxxs": text.length > 7,
  };
}

function SpendToRevComparator({
  metricsById,
  aggregratedMetricsById,
  attributionModel,
  attributionWindow,
}: {
  metricsById: Record<string, SalesPageMetric>;
  aggregratedMetricsById: Record<string, number>;
  attributionModel: ReportBodyState["attributionModel"];
  attributionWindow: ReportBodyState["attributionWindow"];
}) {
  const { user } = useUser();

  const revenueId = `Attributed Rev__${attributionModel}__${attributionWindow}____accrual`;
  const revenueMetric = metricsById[revenueId] as SalesPageMetric | undefined;
  const spendMetrics = metricsById.spend as SalesPageMetric | undefined;
  if (!revenueMetric || !spendMetrics) {
    return null;
  }
  return (
    <div
      className={clsx(
        "border border-v2-secondary gap-2 h-[3rem] w-full rounded-md mt-2",
        "p-2 flex flex-row items-center justify-between	text-xs",
      )}
    >
      <div className="flex flex-row">
        <div className="font-semibold">{spendMetrics.name}</div>
        <div className="ml-1">
          <GenericTooltip
            content={revenueMetric.formatter(
              aggregratedMetricsById.spend,
              user.currencies.currentCurrency,
              { precision: "exact" },
            )}
            noInfoCircle
          >
            <MetricValue
              as="span"
              format={revenueMetric.format}
              precision="approximate"
              value={aggregratedMetricsById.spend}
            />
          </GenericTooltip>
        </div>
      </div>
      <i className="fa-regular fa-sharp fa-arrow-right" />
      <div className="flex flex-row items-center">
        <div className="font-semibold">{revenueMetric.name}</div>
        <div className="ml-1">
          <GenericTooltip
            content={revenueMetric.formatter(
              aggregratedMetricsById[revenueId],
              user.currencies.currentCurrency,
              { precision: "exact" },
            )}
            noInfoCircle
          >
            <MetricValue
              as="span"
              format={revenueMetric.format}
              precision="approximate"
              value={aggregratedMetricsById[revenueId]}
            />
          </GenericTooltip>
        </div>
      </div>
    </div>
  );
}

export function MiniGraph({
  data,
  average,
  isPositiveChangeGood,
}: {
  isPositiveChangeGood: boolean;
  data: number[];
  average: number;
}) {
  let width: number;
  let height: number;
  let gradient: any;

  function getGradient(ctx: any, chartArea: any) {
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (!gradient || width !== chartWidth || height !== chartHeight) {
      /* Create the gradient because this is either the first render
         or the size of the chart has changed */
      width = chartWidth;
      height = chartHeight;
      gradient = ctx.createLinearGradient(
        0,
        chartArea.bottom,
        0,
        chartArea.top,
      );
      gradient.addColorStop(isPositiveChangeGood ? 0 : 1, "#E2230A");
      gradient.addColorStop(0.3, "#FFE9A3");
      gradient.addColorStop(isPositiveChangeGood ? 1 : 0, "#218F27");
    }

    return gradient;
  }

  const labels = new Array(data.length).fill(1);

  const dataPacket = {
    labels: labels,
    datasets: [
      {
        yAxisID: "metricY",
        fill: false,
        borderColor: function (context: any) {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            // This case happens on initial chart load
            return;
          }
          return getGradient(ctx, chartArea);
        },
        data,

        pointRadius: 0,
        tension: 0.4,
        borderWidth: 2,
        spanGaps: true,
      },
      {
        yAxisID: "metricY",
        fill: false,
        borderColor: "#021330",
        data: new Array(data.length).fill(average),
        borderDash: [2.5, 2.5],
        pointRadius: 0,
        borderWidth: 0.75,
        animation: false,
      },
    ] as ChartDataset<"line", number[]>[],
  };

  return (
    <Line
      options={{
        normalized: true,
        animation: false,
        hover: {
          mode: "null",
        } as any,

        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: {
            display: false,
          },

          tooltip: {
            enabled: false,
          },
        },
        scales: {
          xAxis: {
            type: "category",
            display: false,
          },
          metricY: {
            type: "linear",
            display: false,
            position: "left",
          },
        },
      }}
      data={dataPacket}
    />
  );
}
